import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Typography,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import { TServiceValuationTaskError, TValuationFormTask } from "../../types";
import ErrorIcon from "@material-ui/icons/Error";
import cuideoTheme from "../../../../../../../../containers/themes/defaultTheme";

const StyledDialog = styled(Dialog)<{ error?: string }>`
  .MuiPaper-root {
    transition: background-color 0.1s ease-in-out;
    background-color: #fff;
  }

  ${({ error }) => {
    if (error) {
      return `
        .MuiPaper-root {
          color: ${cuideoTheme.palette.error.contrastText};
          background-color: ${cuideoTheme.palette.error.dark};

          .MuiTypography-h6 {
            color: ${cuideoTheme.palette.error.contrastText};
          }
        }
      `;
    }
  }}
`;

const StyledLinearProgress = styled(LinearProgress)`
  height: 0.625rem;
  border-radius: 0.375rem;
  margin: 1rem 0;

  .MuiLinearProgress-bar {
    border-radius: 0.375rem;
  }
`;

const StyledErrorsTitle = styled(Typography)`
  font-size: 1rem;
  line-height: 1.35;
  color: #fff;
`;

const StyledErrorsList = styled(Box)`
  padding: 0.5rem 0 1rem 0.5rem;
`;

const StyledErrorItem = styled(Typography)`
  font-size: 0.875rem;
  color: #fff;
`;

interface IValuationProcessProps {
  open: boolean;
  tasks: TValuationFormTask[];
}

const ValuationProcess = (props: IValuationProcessProps) => {
  const { open, tasks } = props;
  const { formatMessage } = useIntl();
  const [state, setState] = useState({
    totalTasks: 0,
    completedTasks: 0,
    progress: 0,
    errors: [] as TServiceValuationTaskError[],
  });

  useEffect(() => {
    let totalTasks = 0;
    let completedTasks = 0;
    const errors: TServiceValuationTaskError[] = [];
    tasks.forEach((task) => {
      totalTasks++;
      if (task.status === "success") {
        completedTasks++;
      }
      if (task.status === "error") {
        if (task.errors) {
          errors.push(...task.errors);
        } else {
          errors.push("error_unknown");
        }
      }
    });
    const progress =
      totalTasks === 0 ? 0 : Math.round((completedTasks / totalTasks) * 100);

    setState({
      totalTasks,
      completedTasks,
      progress,
      errors,
    });
  }, [tasks]);

  const formattedErrors: React.ReactNode[] = [];
  state.errors.forEach((error, i) => {
    let message: string | null = null;
    if (error === "error_creating_service_holder") {
      message = formatMessage({
        id: "ValuationProcess.Error.CreatingServiceHolder",
        defaultMessage: "Error creando el usuario",
      });
    }
    if (error === "error_updating_service_holder") {
      message = formatMessage({
        id: "ValuationProcess.Error.UpdatingServiceHolder",
        defaultMessage: "Error actualizando el usuario",
      });
    }
    if (error === "error_updating_service") {
      message = formatMessage({
        id: "ValuationProcess.Error.UpdatingService",
        defaultMessage: "Error actualizando la información del servicio",
      });
    }
    if (error === "error_creating_new_relationship") {
      message = formatMessage({
        id: "ValuationProcess.Error.CreatingNewRelationship",
        defaultMessage:
          "Error creando una nueva relación entre usuario y contacto",
      });
    }
    if (error === "error_creating_new_contact") {
      message = formatMessage({
        id: "ValuationProcess.Error.CreatingNewContact",
        defaultMessage:
          "Error creando un nuevo contacto para usar como persona de contacto",
      });
    }
    if (error === "error_removing_existing_relation") {
      message = formatMessage({
        id: "ValuationProcess.Error.RemovingExistingRelationship",
        defaultMessage: "Error borrando un contacto como persona de contacto",
      });
    }
    if (error === "error_modifying_existing_relation") {
      message = formatMessage({
        id: "ValuationProcess.Error.ModyfingExistingRelationship",
        defaultMessage: "Error modificando relación entre usuario y contactos",
      });
    }
    if (error === "error_unknown") {
      message = formatMessage({
        id: "ValuationProcess.Error.Unknown",
        defaultMessage: "Error desconocido",
      });
    }

    if (message) {
      formattedErrors.push(<StyledErrorItem>{message}</StyledErrorItem>);
    }
  });

  return (
    <StyledDialog
      open={open}
      error={state.errors.length > 0 ? "error" : undefined}
    >
      <DialogTitle>
        <FormattedMessage
          id="ValuationProcess.Valuating"
          defaultMessage="Valorando servicio"
        />
      </DialogTitle>
      <DialogContent>
        {state.errors.length > 0 ? (
          <Grid container spacing={8} alignItems="flex-start" wrap="nowrap">
            <Grid item>
              <ErrorIcon />
            </Grid>
            <Grid item>
              <StyledErrorsTitle>
                <FormattedMessage
                  id="ValuationProcess.ErrorsTitle"
                  defaultMessage="Ha habido errores al intentar validar el servicio"
                />
              </StyledErrorsTitle>
              <StyledErrorsList>{formattedErrors}</StyledErrorsList>
              <StyledErrorsTitle>
                <FormattedMessage
                  id="ValuationProcess.ErrorsTittleBottom"
                  defaultMessage="Puedes recargar la página e intentar repetir la operación aunque es posible que se hayan perdido datos o que solo se hayan actualizado parcialmente"
                />
              </StyledErrorsTitle>
            </Grid>
          </Grid>
        ) : (
          <StyledLinearProgress variant="determinate" value={state.progress} />
        )}
      </DialogContent>
    </StyledDialog>
  );
};

export default ValuationProcess;
